import { useAppSelector } from '@/app/hooks';
import {
  useUpdateFileMutation,
  useUploadFileMutation,
} from '@/features/api/endpoints/fileEndpoints';
import { useIsAdmin } from '@/shared/auth/useDirectusUser';
import { useIsXs } from '@/shared/utils/mediaQuery-utils';
import { getUploadErrorKey } from '@/shared/utils/upload-utils';
import { ActionIcon, Button, Stack, Text, Tooltip } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconUpload } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';

const UploadFileBtn = () => {
  const { t } = useTranslate();
  const isXs = useIsXs();
  const isAdmin = useIsAdmin();

  const currentFolderId = useAppSelector(state => state.drive.currentFolderId);
  const [updateFile] = useUpdateFileMutation();
  const [uploadFile] = useUploadFileMutation();

  if (!isAdmin) {
    return null;
  }

  const handleUploadFile = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const uploadedFile = await uploadFile(formData).unwrap();

      if (currentFolderId) {
        await updateFile({
          body: { folder: currentFolderId },
          id: uploadedFile.id,
        }).unwrap();
      }

      notifications.show({
        color: 'green',
        message: t('file.upload.success'),
      });

      modals.closeAll();
    } catch (error: unknown) {
      notifications.show({
        color: 'red',
        message: t(getUploadErrorKey(error, 'file.upload.error')),
      });
    }
  };

  const handleUploadClick = () => {
    modals.open({
      title: t('file.upload.title'),
      fullScreen: isXs,
      children: (
        <Stack gap="sm">
          <Dropzone
            onDrop={files => {
              const selectedFile = files[0];

              if (selectedFile) {
                void handleUploadFile(selectedFile);
              }
            }}
            onReject={() => {
              notifications.show({
                color: 'red',
                message: t('file.upload.error'),
              });
            }}
            multiple={false}
            maxFiles={1}
          >
            <Stack gap="xs" align="center" py="md">
              <IconUpload size={28} />
              <Text size="sm" ta="center">
                {t('file.upload.dropzone.help')}
              </Text>
            </Stack>
          </Dropzone>
        </Stack>
      ),
    });
  };

  return (
    <>
      <Tooltip label={t('file.upload.title')} withArrow>
        <ActionIcon
          onClick={handleUploadClick}
          aria-label={t('file.upload.title')}
          hiddenFrom="sm"
          variant="light"
        >
          <IconUpload size={18} />
        </ActionIcon>
      </Tooltip>
      <Button
        leftSection={<IconUpload size={18} />}
        variant="light"
        onClick={handleUploadClick}
        visibleFrom="sm"
      >
        {t('file.upload.title')}
      </Button>
    </>
  );
};

export default UploadFileBtn;
